import { api } from '@/lib/api/api';
import { IHour, IResource, Office } from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';
import { useQuery } from '@tanstack/react-query';

export function useHours(office: Office) {
  const hours = useQuery(['hours.all', office.id], async () => {
    const res = await api.client.hours.all(office.id);
    return res.result;
  });
  return hours;
}

export function useHourRules(office: Office, resource?: IResource) {
  const hours = useQuery(['hours.rules', office.id, resource?.id], async () => {
    const res = await api.client.hours.rules(office.id, resource?.id);
    return res.result;
  });
  return hours;
}

export function useBulkHourRules(office: Office, resources: IResource[] = []) {
  const hours = useQuery(
    ['hours.rules', office.id, resources.map((x) => x.id)],
    async () => {
      const promises =
        resources.length === 0
          ? [api.client.hours.rules(office.id)]
          : resources.map((resource) =>
              api.client.hours.rules(office.id, resource?.id)
            );
      return Promise.all(promises).then((all) => all.map((x) => x.result));
    }
  );
  return hours;
}

export function useEmployeeHours() {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const offices = (authenticatedUser?.offices?.offices ?? []).map((x) => x.id);
  const byId = authenticatedUser?.offices?.byId ?? {};
  const hours = useQuery(['hours.rules', offices], async () => {
    const promises = offices.map((id) => api.client.hours.rules(id));
    const results: (IHour & { office: Office })[] = [];
    const all = await Promise.all(promises).then((all) =>
      all.map((x) => x.result)
    );
    for (const resGetAllHour of all) {
      const elements = resGetAllHour?.elements ?? [];
      for (const hour of elements) {
        const office = byId[hour.office_id];
        results.push({
          ...hour,
          office
        });
      }
    }
    return results;
  });
  return hours;
}

export function useHourHolidays(office: Office) {
  const hours = useQuery(['hours.holidays', office.id], async () => {
    const res = await api.client.hours.holidays(office.id);
    return res.result;
  });
  return hours;
}

export function useOperationalHours(office?: { id: string }) {
  return useQuery(
    ['hours.rules', office?.id],
    async () => {
      const res = await api.client.hours.weekdays(office?.id ?? '');
      return res.result;
    },
    {
      enabled: !!office?.id
    }
  );
}
